'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["size", "colorScheme", "variant", "className", "spacing", "isAttached", "isDisabled", "orientation"],
  _excluded2 = ["children", "className"],
  _excluded3 = ["label", "placement", "spacing", "children", "className", "__css"],
  _excluded4 = ["isDisabled", "isLoading", "isActive", "children", "leftIcon", "rightIcon", "loadingText", "iconSpacing", "type", "spinner", "spinnerPlacement", "className", "as"],
  _excluded5 = ["icon", "children", "isRound", "aria-label"];
import { createContext } from "../react-context";
import { forwardRef, chakra, useStyleConfig, omitThemingProps } from "../system";
import { cx, dataAttr } from "../shared-utils";
import { useMemo, useState, useCallback, isValidElement, cloneElement } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Spinner } from "../spinner";
import { useMergeRefs } from "../react-use-merge-refs";

// src/button-context.ts
var [ButtonGroupProvider, useButtonGroup] = createContext({
  strict: false,
  name: "ButtonGroupContext"
});
var attachedStyles = {
  horizontal: {
    "> *:first-of-type:not(:last-of-type)": {
      borderEndRadius: 0
    },
    "> *:not(:first-of-type):not(:last-of-type)": {
      borderRadius: 0
    },
    "> *:not(:first-of-type):last-of-type": {
      borderStartRadius: 0
    }
  },
  vertical: {
    "> *:first-of-type:not(:last-of-type)": {
      borderBottomRadius: 0
    },
    "> *:not(:first-of-type):not(:last-of-type)": {
      borderRadius: 0
    },
    "> *:not(:first-of-type):last-of-type": {
      borderTopRadius: 0
    }
  }
};
var gapStyles = {
  horizontal: spacing => ({
    "& > *:not(style) ~ *:not(style)": {
      marginStart: spacing
    }
  }),
  vertical: spacing => ({
    "& > *:not(style) ~ *:not(style)": {
      marginTop: spacing
    }
  })
};
var ButtonGroup = forwardRef(function ButtonGroup2(props, ref) {
  const {
      size,
      colorScheme,
      variant,
      className,
      spacing = "0.5rem",
      isAttached,
      isDisabled,
      orientation = "horizontal"
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const _className = cx("chakra-button__group", className);
  const context = useMemo(() => ({
    size,
    colorScheme,
    variant,
    isDisabled
  }), [size, colorScheme, variant, isDisabled]);
  let groupStyles = Object.assign({
    display: "inline-flex"
  }, isAttached ? attachedStyles[orientation] : gapStyles[orientation](spacing));
  const isVertical = orientation === "vertical";
  return /* @__PURE__ */jsx(ButtonGroupProvider, {
    value: context,
    children: /* @__PURE__ */jsx(chakra.div, Object.assign({
      ref,
      role: "group",
      __css: groupStyles,
      className: _className,
      "data-attached": isAttached ? "" : void 0,
      "data-orientation": orientation,
      flexDir: isVertical ? "column" : void 0
    }, rest))
  });
});
ButtonGroup.displayName = "ButtonGroup";

// src/use-button-type.tsx
function useButtonType(value) {
  const [isButton, setIsButton] = useState(!value);
  const refCallback = useCallback(node => {
    if (!node) return;
    setIsButton(node.tagName === "BUTTON");
  }, []);
  const type = isButton ? "button" : void 0;
  return {
    ref: refCallback,
    type
  };
}

// src/button-icon.tsx
function ButtonIcon(props) {
  const {
      children,
      className
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded2);
  const _children = /*#__PURE__*/isValidElement(children) ? /*#__PURE__*/cloneElement(children, {
    "aria-hidden": true,
    focusable: false
  }) : children;
  const _className = cx("chakra-button__icon", className);
  return /* @__PURE__ */jsx(chakra.span, Object.assign({
    display: "inline-flex",
    alignSelf: "center",
    flexShrink: 0
  }, rest, {
    className: _className,
    children: _children
  }));
}
ButtonIcon.displayName = "ButtonIcon";

// src/button-spinner.tsx
function ButtonSpinner(props) {
  const {
      label,
      placement,
      spacing = "0.5rem",
      children = /* @__PURE__ */jsx(Spinner, {
        color: "currentColor",
        width: "1em",
        height: "1em"
      }),
      className,
      __css
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded3);
  const _className = cx("chakra-button__spinner", className);
  const marginProp = placement === "start" ? "marginEnd" : "marginStart";
  const spinnerStyles = useMemo(() => Object.assign({
    display: "flex",
    alignItems: "center",
    position: label ? "relative" : "absolute",
    [marginProp]: label ? spacing : 0,
    fontSize: "1em",
    lineHeight: "normal"
  }, __css), [__css, label, marginProp, spacing]);
  return /* @__PURE__ */jsx(chakra.div, Object.assign({
    className: _className
  }, rest, {
    __css: spinnerStyles,
    children
  }));
}
ButtonSpinner.displayName = "ButtonSpinner";
var Button = forwardRef((props, ref) => {
  const group = useButtonGroup();
  const styles = useStyleConfig("Button", Object.assign({}, group, props));
  const _omitThemingProps = omitThemingProps(props),
    {
      isDisabled = group == null ? void 0 : group.isDisabled,
      isLoading,
      isActive,
      children,
      leftIcon,
      rightIcon,
      loadingText,
      iconSpacing = "0.5rem",
      type,
      spinner,
      spinnerPlacement = "start",
      className,
      as
    } = _omitThemingProps,
    rest = _objectWithoutPropertiesLoose(_omitThemingProps, _excluded4);
  const buttonStyles = useMemo(() => {
    const _focus = Object.assign({}, styles == null ? void 0 : styles["_focus"], {
      zIndex: 1
    });
    return Object.assign({
      display: "inline-flex",
      appearance: "none",
      alignItems: "center",
      justifyContent: "center",
      userSelect: "none",
      position: "relative",
      whiteSpace: "nowrap",
      verticalAlign: "middle",
      outline: "none"
    }, styles, !!group && {
      _focus
    });
  }, [styles, group]);
  const {
    ref: _ref,
    type: defaultType
  } = useButtonType(as);
  const contentProps = {
    rightIcon,
    leftIcon,
    iconSpacing,
    children
  };
  return /* @__PURE__ */jsxs(chakra.button, Object.assign({
    ref: useMergeRefs(ref, _ref),
    as,
    type: type != null ? type : defaultType,
    "data-active": dataAttr(isActive),
    "data-loading": dataAttr(isLoading),
    __css: buttonStyles,
    className: cx("chakra-button", className)
  }, rest, {
    disabled: isDisabled || isLoading,
    children: [isLoading && spinnerPlacement === "start" && /* @__PURE__ */jsx(ButtonSpinner, {
      className: "chakra-button__spinner--start",
      label: loadingText,
      placement: "start",
      spacing: iconSpacing,
      children: spinner
    }), isLoading ? loadingText || /* @__PURE__ */jsx(chakra.span, {
      opacity: 0,
      children: /* @__PURE__ */jsx(ButtonContent, Object.assign({}, contentProps))
    }) : /* @__PURE__ */jsx(ButtonContent, Object.assign({}, contentProps)), isLoading && spinnerPlacement === "end" && /* @__PURE__ */jsx(ButtonSpinner, {
      className: "chakra-button__spinner--end",
      label: loadingText,
      placement: "end",
      spacing: iconSpacing,
      children: spinner
    })]
  }));
});
Button.displayName = "Button";
function ButtonContent(props) {
  const {
    leftIcon,
    rightIcon,
    children,
    iconSpacing
  } = props;
  return /* @__PURE__ */jsxs(Fragment, {
    children: [leftIcon && /* @__PURE__ */jsx(ButtonIcon, {
      marginEnd: iconSpacing,
      children: leftIcon
    }), children, rightIcon && /* @__PURE__ */jsx(ButtonIcon, {
      marginStart: iconSpacing,
      children: rightIcon
    })]
  });
}
var IconButton = forwardRef((props, ref) => {
  const {
      icon,
      children,
      isRound,
      "aria-label": ariaLabel
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded5);
  const element = icon || children;
  const _children = /*#__PURE__*/isValidElement(element) ? /*#__PURE__*/cloneElement(element, {
    "aria-hidden": true,
    focusable: false
  }) : null;
  return /* @__PURE__ */jsx(Button, Object.assign({
    padding: "0",
    borderRadius: isRound ? "full" : void 0,
    ref,
    "aria-label": ariaLabel
  }, rest, {
    children: _children
  }));
});
IconButton.displayName = "IconButton";
export { Button, ButtonGroup, ButtonSpinner, IconButton, useButtonGroup };